/**
 * SortingModal UI Component
 *
 * This class manages the sorting modal for the search results page, allowing users to select sorting options
 * for homes or communities. It handles:
 * - Rendering the appropriate sorting options based on the current product type.
 * - Managing the modal's lifecycle and event binding/unbinding.
 * - Dispatching sort actions to the Redux store when the user selects a new sort option.
 * - Integrating with the modal service to display and clear the modal UI.
 *
 * The SortingModal ensures a consistent and interactive sorting experience for users
 * across different product types in the search results interface.
 */

import { store } from "@redux/store";
import { ProductType, searchParametersActions } from "features/searchResultsFacets";
import modalService from "layout/services/modalService";

export default class SortingModal {
  constructor() {
    this.id = 'sorting-modal';
    this.productType = null;
    this.currentSortBy = null;
    this.currentSortOrder = null;
    this.onClickContent = null;
  }


 /**
   * Sets the metadata for the SortingModal instance.
   *
   * - Updates the productType property based on the provided metadata object.
   * - This determines which set of sorting options will be rendered in the modal.
   *
   * @param {Object} metadata - An object containing configuration for the modal.
  */
  setMetadata(metadata) {
    this.productType = metadata?.productType;
    this.currentSortBy = metadata?.sorting?.sortBy;
    this.currentSortOrder = metadata?.sorting?.sortOrder;
  }

 /**
   * Handles the sort option change event in the sorting modal.
   *
   * - Extracts the selected sort option and order from the clicked input element.
   * - If the selected sort option and order are unchanged, does nothing.
   * - Otherwise, updates the currentSortBy and currentSortOrder properties.
   * - Dispatches a fetchSort action to the Redux store with the new sort parameters.
   * - Clears the modal UI after dispatching the sort change.
   *
   * @param {Event} event - The click event triggered on the sorting modal.
  */
  dispatchSortChangeEvent(event) {
    const isCloseButton = event.target.closest('button[data-close-modal]');
    if (isCloseButton) {
      modalService.clearHtml();
      return;
    }

    const isSubmitButton = event.target.closest('button[data-sort-result]');
    if (!isSubmitButton) {
      return;
    }

    const modal = this.getModal();
    const selectedOption = modal.querySelector('input[name="sortOptions"]:checked');
    const value = selectedOption.closest('li').getAttribute('data-option-value');
    const sortBy = value.split(',')[0];
    const sortOrder = value.split(',')[1] === 'true';

    if (this.currentSortBy === sortBy && this.currentSortOrder === sortOrder) {
      modalService.clearHtml();
      return;
    }

    this.currentSortBy = sortBy;
    this.currentSortOrder = sortOrder;

    store.dispatch(searchParametersActions.fetchSort({
      sortBy,
      sortOrder,
    }));

    modalService.clearHtml();
  }

/**
  * Binds event listeners to the sorting modal UI.
  *
  * - Retrieves the modal element by its ID.
  * - Binds the click event to the modal, using the dispatchSortChangeEvent handler.
  * - Binds the close button click to clear the modal.
  * - Stores the bound handler reference for later removal.
  *
  * This ensures that user interactions with the sorting options are handled and dispatched correctly.
  */
  bindEvents() {
    const modal = this.getModal();
    modal.parentElement.classList.add('modal--sorting');
    this.onClickContent = this.dispatchSortChangeEvent.bind(this);
    modal.addEventListener('click', this.onClickContent);
  }

/**
  * Unbinds event listeners from the sorting modal UI.
  *
  * - Retrieves the modal element by its ID.
  * - Removes the click event listener that was previously bound using bindEvents.
  * - Removes the close button click listener using the stored handler reference.
  * - Ensures that no duplicate or stale event handlers remain attached when the modal is removed or re-initialized.
  *
  * This helps prevent memory leaks and unintended behavior from lingering event listeners.
  */
  unbindEvents() {
    const modal = this.getModal();
    modal.parentElement.classList.remove('modal--sorting');
    modal.removeEventListener('click', this.onClickContent);
  }

/**
  * Retrieves the DOM element representing the sorting modal by its unique ID.
  *
  * @returns {HTMLElement|null} The modal element if found, otherwise null.
  */
  getModal() {
    return document.getElementById(this.id);
  }

  /**
   * Helper to generate a radio option label.
   * @param {string} label
   * @param {string} value
   * @returns {string}
   */
  getRadioOption(label, value) {
    const dataQa = `sort-${label.toLowerCase().replace(/[\s#]/g, '')}`;
    const isChecked = (!value && !this.currentSortBy) || value === `${this.currentSortBy},${this.currentSortOrder}`;
    const checkedAttr = isChecked ? 'checked' : '';
    const sortOption = `
      <li class="nhs-c-input__container--with-text nhs-u-cursor--pointer" data-option-value="${value}">
        <input class="nhs-c-input__radio" id="chk-${value}" data-qa="${dataQa}" type="radio" name="sortOptions" value="${value}" ${checkedAttr}>
        <label for="chk-${value}">${label}</label>
      </li>
    `;

    return sortOption;
  }

  /**
   * Generates and returns the HTML string for the sorting modal for a given options array.
   * @param {Array<{label: string, value: string}>} options
   * @returns {string}
   */
  getHtmlForOptions(options) {
    return `
      <div id='${this.id}' data-qa="sort-modal" class="nhs-u-width--full nhs-u-flex-column nhs-u-height--full">
        <header class="nhs-u-padding--600 nhs-u-flex nhs-u-items-center nhs-u-justify-content-space-between">
          <h2 class="nhs-o-typography--souvenir-heading-1-extra-large-light nhs-u-margin--0">Sort results</h2>
          <button class="nhs-u-margin-left--auto nhs-u-padding--100" data-close-modal data-qa="sort-close-modal" aria-label="Close modal">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M1.0256 0.332239C0.831519 0.204074 0.567556 0.225828 0.396692 0.396692C0.225828 0.567555 0.204075 0.831519 0.332239 1.0256L0.396692 1.10372L6.29318 7.00021L0.396691 12.8967L0.332238 12.9748C0.204074 13.1689 0.225829 13.4329 0.396691 13.6037C0.567555 13.7746 0.831519 13.7963 1.0256 13.6682L1.10372 13.6037L7.00021 7.70724L12.8967 13.6037L12.9748 13.6682C13.1689 13.7963 13.4329 13.7746 13.6037 13.6037C13.7746 13.4329 13.7963 13.1689 13.6682 12.9748L13.6037 12.8967L7.70724 7.00021L13.6037 1.10372L13.6682 1.0256C13.7963 0.831519 13.7746 0.567555 13.6037 0.396692C13.4329 0.225828 13.1689 0.204074 12.9748 0.332238L12.8967 0.396692L7.00021 6.29318L1.10372 0.396692L1.0256 0.332239Z" fill="#1E1D21"/>
            </svg>
          </button>
        </header>
        <fieldset class="nhs-u-height--full nhs-u-padding--600 nhs-u-padding--bottom-800 nhs-u-overflow--auto-y nhs-u-overflow--hidden-x">
          <div class="nhs-o-typography--sans-body-1-small-book nhs-u-margin--bottom-300">Sort my results by</div>
          <ul class="nhs-u-flex-column nhs-u-items-flex-start nhs-u-gap--200 nhs-u-flex--1">
            ${options.map(opt => this.getRadioOption(opt.label, opt.value)).join('')}
          </ul>
        </fieldset>        
        <footer class="nhs-u-padding--300-600 nhs-u-border-top nhs-u-shadow--elevation-top-small nhs-u-margin--top-300">
          <button data-sort-result data-qa="sort-apply" class="nhs-n1-c-button--secondary-dark nhs-c-button--text-only nhs-c-button--full nhs-c-button--medium-40">Sort results</button>
        </footer>
      </div>
    `;
  }

/**
  * Generates and returns the HTML string for the sorting modal when displaying home search results.
  *
  * - Renders a set of radio button options for sorting homes by various criteria
  *     (price, beds, sqft, city, builder).
  * - Each option is wrapped in a label for accessibility and clickability.
  * - The generated HTML is used as the content for the sorting modal when the product type is 'Homes'.
  *
  * @returns {string} The HTML string for the homes sorting modal UI.
  */
  getHtmlForHomes() {
    const options = [
      { label: 'Default', value: '' },
      { label: 'Price (Low-High)', value: 'price,false' },
      { label: 'Price (High-Low)', value: 'price,true' },
      { label: '# Beds (Low-High)', value: 'bed,false' },
      { label: '# Beds (High-Low)', value: 'bed,true' },
      { label: 'SqFt (Low-High)', value: 'sqft,false' },
      { label: 'SqFt (High-Low)', value: 'sqft,true' },
      { label: 'City (A-Z)', value: 'city,false' },
      { label: 'Builder (A-Z)', value: 'builders,false' },
    ];
    return this.getHtmlForOptions(options);
  }

/**
  * Generates and returns the HTML string for the sorting modal when displaying community search results.
  *
  * - Renders a set of radio button options for sorting communities by various criteria
  *     (price, review rating, sqft, city, community, builder).
  * - Each option is wrapped in a label for accessibility and clickability.
  * - The generated HTML is used as the content for the sorting modal when the product type is 'Communities'.
  *
  * @returns {string} The HTML string for the communities sorting modal UI.
  */
  getHtmlForCommunities() {
    const options = [
      { label: 'Default', value: '' },
      { label: 'Price (Low-High)', value: 'price,false' },
      { label: 'Price (High-Low)', value: 'price,true' },
      { label: 'Best Review Rating', value: 'reviewrating,true' },
      { label: 'SqFt (Low-High)', value: 'sqft,false' },
      { label: 'SqFt (High-Low)', value: 'sqft,true' },
      { label: 'City (A-Z)', value: 'city,false' },
      { label: 'Community (A-Z)', value: 'community,false' },
      { label: 'Builder (A-Z)', value: 'builders,false' },
    ];
    return this.getHtmlForOptions(options);
  }

/**
  * Returns the HTML string for the sorting modal UI based on the current product type.
  *
  * - If the product type is 'Homes', returns the homes sorting modal HTML.
  * - For all other product types (including 'Communities'), returns the communities sorting modal HTML.
  *
  * @returns {string} The HTML string for the sorting modal UI.
  */
  getHtml() {
    switch (this.productType) {
      case ProductType.Homes:
        return this.getHtmlForHomes();
      default:
        return this.getHtmlForCommunities();
    }
  }
}